import { promises as fs } from 'node:fs';
import path from 'node:path';
import type { Theme } from '../types';

// EPIPE error handler tracker to avoid duplicate handlers
let epipeHandlerInstalled = false;

/**
 * Install EPIPE error handler safely (only once per process)
 * Prevents crashes when output is piped to head/tail commands
 */
function installEPIPEHandler(): void {
  if (epipeHandlerInstalled || !process.stdout || typeof process.stdout.on !== 'function') {
    return;
  }

  const existingListeners = process.stdout.listenerCount('error');
  if (existingListeners === 0) {
    process.stdout.on('error', (err: NodeJS.ErrnoException) => {
      if (err.code === 'EPIPE') {
        // Silently ignore EPIPE errors (broken pipe when output is piped to head/tail)
        process.exit(0);
      }
      // Re-throw other errors for proper debugging
      throw err;
    });
    epipeHandlerInstalled = true;
  }
}

// Install handler when module is loaded
installEPIPEHandler();

interface CachedTheme {
  theme: Theme;
  loadedAt: number;
  accessCount: number;
}

export class ThemeLoader {
  private themesDir: string;
  private loadedThemes: Map<string, CachedTheme> = new Map();
  private cacheMaxAge: number = 3600000; // 1 hour in milliseconds
  private maxCacheSize: number = 10; // Maximum number of cached themes

  constructor(themesDir?: string) {
    this.themesDir = themesDir || 'themes';
    this.safeDebug(`ThemeLoader: Initialized with themes directory: ${this.themesDir}`);
  }

  async load(themeName: string): Promise<Theme> {
    // Check cache and validate age
    const cached = this.loadedThemes.get(themeName);
    if (cached && this.isCacheValid(cached)) {
      this.safeDebug(
        `ThemeLoader: Using cached theme '${themeName}' (access #${cached.accessCount + 1})`
      );
      cached.accessCount++;
      return cached.theme;
    }

    try {
      // First try to load from CSS file
      console.log(`ThemeLoader: Loading theme '${themeName}' from file system`);
      const theme = await this.loadFromFile(themeName);
      this.cacheTheme(themeName, theme);
      return theme;
    } catch (_error) {
      // Fallback to our professional built-in themes (keep current good formatting)
      console.log(`ThemeLoader: Loading theme '${themeName}' (professional built-in)`);

      const theme: Theme = {
        name: themeName,
        metadata: {
          name: themeName,
          version: '2.0.0',
          description: `${themeName} professional theme`,
          author: 'MDExport',
        },
        styles: [this.generateProfessionalTheme(themeName)],
        templates: {},
      };

      this.cacheTheme(themeName, theme);
      return theme;
    }
  }

  /**
   * Load theme from external CSS file
   */
  private async loadFromFile(themeName: string): Promise<Theme> {
    const cssFilePath = path.join(this.themesDir, `${themeName}.css`);

    const cssContent = await fs.readFile(cssFilePath, 'utf-8');

    return {
      name: themeName,
      metadata: {
        name: themeName,
        version: '2.0.0',
        description: `${themeName} theme from file`,
        author: 'External CSS',
      },
      styles: [cssContent],
      templates: {},
    };
  }

  /**
   * Cache a theme with metadata
   */
  private cacheTheme(themeName: string, theme: Theme): void {
    // Enforce cache size limit using LRU strategy
    if (this.loadedThemes.size >= this.maxCacheSize) {
      this.evictLeastUsedTheme();
    }

    this.loadedThemes.set(themeName, {
      theme,
      loadedAt: Date.now(),
      accessCount: 1,
    });

    this.safeDebug(
      `ThemeLoader: Cached theme '${themeName}' (cache size: ${this.loadedThemes.size}/${this.maxCacheSize})`
    );
  }

  /**
   * Check if cached theme is still valid
   */
  private isCacheValid(cached: CachedTheme): boolean {
    const age = Date.now() - cached.loadedAt;
    return age < this.cacheMaxAge;
  }

  /**
   * Evict least recently used theme from cache
   */
  private evictLeastUsedTheme(): void {
    let leastUsed: [string, CachedTheme] | null = null;

    for (const entry of this.loadedThemes.entries()) {
      if (!leastUsed || entry[1].accessCount < leastUsed[1].accessCount) {
        leastUsed = entry;
      }
    }

    if (leastUsed) {
      this.loadedThemes.delete(leastUsed[0]);
      this.safeDebug(`ThemeLoader: Evicted theme '${leastUsed[0]}' from cache (LRU)`);
    }
  }

  /**
   * Clear theme cache
   */
  clearCache(): void {
    const size = this.loadedThemes.size;
    this.loadedThemes.clear();
    this.safeDebug(`ThemeLoader: Cleared theme cache (${size} themes removed)`);
  }

  /**
   * Safe debug logging that handles EPIPE errors gracefully
   */
  private safeDebug(message: string): void {
    try {
      console.debug(message);
    } catch (error) {
      // Silently handle EPIPE errors when stdout is closed (e.g., from piping to head)
      if (error instanceof Error && 'code' in error && error.code !== 'EPIPE') {
        // Re-throw non-EPIPE errors
        throw error;
      }
      // EPIPE errors are expected when using pipes, silently ignore
    }
  }

  /**
   * Get cache statistics
   */
  getCacheStats(): { size: number; themes: string[]; totalAccessCount: number } {
    const themes = Array.from(this.loadedThemes.keys());
    const totalAccessCount = Array.from(this.loadedThemes.values()).reduce(
      (sum, cached) => sum + cached.accessCount,
      0
    );

    return {
      size: this.loadedThemes.size,
      themes,
      totalAccessCount,
    };
  }

  /**
   * Preload themes for better performance
   */
  async preloadThemes(themeNames: string[]): Promise<void> {
    console.log(`ThemeLoader: Preloading ${themeNames.length} themes...`);

    const promises = themeNames.map((name) =>
      this.load(name).catch((err) =>
        console.warn(`ThemeLoader: Failed to preload theme '${name}':`, err)
      )
    );

    await Promise.all(promises);
    console.log(`ThemeLoader: Preloading complete (cache size: ${this.loadedThemes.size})`);
  }

  async listThemes(): Promise<string[]> {
    try {
      const files = await fs.readdir(this.themesDir);
      const cssFiles = files
        .filter((file) => file.endsWith('.css'))
        .map((file) => path.basename(file, '.css'));

      // Return file themes + built-in themes
      const builtInThemes = ['academic', 'modern', 'minimal'];
      return [...new Set([...cssFiles, ...builtInThemes])];
    } catch {
      return ['academic', 'modern', 'minimal'];
    }
  }

  /**
   * Generate professional theme CSS (KEEP current good formatting!)
   */
  private generateProfessionalTheme(themeName: string): string {
    const baseStyles = this.getBaseStyles();
    const themeSpecific = this.getThemeSpecificStyles(themeName);
    const printStyles = this.getPrintStyles();
    const codeStyles = this.getCodeHighlightingStyles();

    return `
${baseStyles}

${themeSpecific}

${codeStyles}

${printStyles}
    `.trim();
  }

  private getBaseStyles(): string {
    return `
/* Professional Base Styles */
* {
  box-sizing: border-box;
}

body {
  font-family: 'Charter', 'Georgia', 'Times New Roman', serif;
  font-size: 11pt;
  line-height: 1.5;
  color: #2c3e50;
  margin: 0;
  padding: 0;
  background: white;
}

/* Typography Hierarchy */
h1, h2, h3, h4, h5, h6 {
  font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif;
  font-weight: 600;
  line-height: 1.2;
  margin: 2em 0 0.5em 0;
  page-break-after: avoid;
  break-after: avoid;
}

h1 {
  font-size: 2.5em;
  color: #1a202c;
  border-bottom: 3px solid #3182ce;
  padding-bottom: 0.3em;
  margin-top: 0;
}

h2 {
  font-size: 1.8em;
  color: #2d3748;
  border-bottom: 1px solid #e2e8f0;
  padding-bottom: 0.2em;
}

h3 {
  font-size: 1.4em;
  color: #4a5568;
}

h4 {
  font-size: 1.2em;
  color: #718096;
}

/* Paragraphs and spacing */
p {
  margin: 1em 0;
  text-align: justify;
  hyphens: auto;
}

/* Links */
a {
  color: #3182ce;
  text-decoration: none;
  border-bottom: 1px solid transparent;
  transition: border-color 0.2s;
}

a:hover {
  border-bottom-color: #3182ce;
}

/* Lists */
ul, ol {
  margin: 1em 0;
  padding-left: 2em;
}

li {
  margin: 0.5em 0;
}

/* Blockquotes */
blockquote {
  margin: 1.5em 0;
  padding: 1em 2em;
  border-left: 4px solid #3182ce;
  background: #f7fafc;
  font-style: italic;
  color: #4a5568;
}
`;
  }

  private getThemeSpecificStyles(themeName: string): string {
    switch (themeName) {
      case 'academic':
        return `
/* Academic Theme Enhancements */
body {
  font-family: 'Times New Roman', 'Liberation Serif', serif;
  font-size: 12pt;
}

h1 {
  text-align: center;
  font-size: 2.2em;
  margin-bottom: 1em;
  border: none;
}

h2 {
  font-size: 1.6em;
  margin-top: 2em;
}
`;

      case 'modern':
        return `
/* Modern Theme Enhancements */
body {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 10.5pt;
}

h1, h2, h3, h4, h5, h6 {
  font-family: 'SF Pro Display', -apple-system, sans-serif;
}
`;

      case 'minimal':
        return `
/* Minimal Theme Enhancements */
body {
  font-family: 'Helvetica Neue', Arial, sans-serif;
  font-size: 11pt;
  color: #333;
}

h1, h2, h3, h4, h5, h6 {
  font-weight: 300;
  color: #000;
  border: none;
}
`;

      default:
        return '/* Default theme styles */';
    }
  }

  private getCodeHighlightingStyles(): string {
    return `
/* Professional Code Highlighting */
pre {
  background: #f8f9fa !important;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 1.5em;
  margin: 1.5em 0;
  overflow-x: auto;
  font-family: 'SF Mono', 'Monaco', 'Cascadia Code', 'Roboto Mono', Consolas, monospace;
  font-size: 9pt;
  line-height: 1.4;
  page-break-inside: avoid;
  break-inside: avoid;
}

code {
  font-family: 'SF Mono', 'Monaco', 'Cascadia Code', 'Roboto Mono', Consolas, monospace;
  font-size: 0.9em;
  background: #f1f3f4;
  padding: 0.2em 0.4em;
  border-radius: 3px;
  color: #d73a49;
}

pre code {
  background: none;
  padding: 0;
  color: #24292e;
  font-size: 9pt;
}

/* Prism.js token styles for better syntax highlighting */
.token.comment,
.token.prolog,
.token.doctype,
.token.cdata {
  color: #6a737d;
  font-style: italic;
}

.token.namespace {
  opacity: 0.7;
}

.token.string,
.token.attr-value {
  color: #032f62;
}

.token.punctuation,
.token.operator {
  color: #24292e;
}

.token.entity,
.token.url,
.token.symbol,
.token.number,
.token.boolean,
.token.variable,
.token.constant,
.token.property,
.token.regex,
.token.inserted {
  color: #005cc5;
}

.token.atrule,
.token.keyword,
.token.attr-name,
.language-autohotkey .token.selector {
  color: #d73a49;
}

.token.function,
.token.deleted,
.token.tag,
.language-autohotkey .token.keyword {
  color: #6f42c1;
}

.token.function-name,
.token.deleted,
.token.tag,
.language-autohotkey .token.keyword {
  color: #005cc5;
}

.token.selector,
.token.attr-name,
.token.string,
.token.char,
.token.builtin,
.token.inserted {
  color: #22863a;
}

.token.class-name {
  color: #6f42c1;
}

.token.important,
.token.bold {
  font-weight: bold;
}

.token.italic {
  font-style: italic;
}
`;
  }

  private getPrintStyles(): string {
    return `
/* Professional Print Styles */
@page {
  size: A4;
  margin: 2.5cm 2cm 2.5cm 2cm;

  @top-center {
    content: string(doctitle);
    font-size: 9pt;
    color: #666;
  }

  @bottom-center {
    content: "Page " counter(page) " of " counter(pages);
    font-size: 9pt;
    color: #666;
  }
}

@media print {
  body {
    font-size: 11pt;
    line-height: 1.4;
    color: #000;
  }

  /* Ensure good page breaks */
  h1, h2, h3, h4, h5, h6 {
    page-break-after: avoid;
    break-after: avoid;
    page-break-inside: avoid;
    break-inside: avoid;
  }

  /* Keep content together */
  pre, blockquote, table {
    page-break-inside: avoid;
    break-inside: avoid;
  }

  img, figure {
    page-break-inside: avoid;
    break-inside: avoid;
    max-width: 100%;
  }

  /* Table improvements */
  table {
    border-collapse: collapse;
    width: 100%;
    margin: 1.5em 0;
  }

  th, td {
    border: 1px solid #ddd;
    padding: 0.5em;
    text-align: left;
    vertical-align: top;
  }

  th {
    background: #f5f5f5;
    font-weight: bold;
  }

  /* Better spacing */
  .page-break {
    page-break-before: always;
    break-before: page;
  }

  /* Print color adjustments */
  * {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  /* Hide elements that shouldn't print */
  .no-print {
    display: none;
  }
}

/* Table styling (for screen and print) */
table {
  border-collapse: collapse;
  width: 100%;
  margin: 1.5em 0;
  font-size: 10pt;
}

th, td {
  border: 1px solid #ddd;
  padding: 0.75em;
  text-align: left;
  vertical-align: top;
}

th {
  background: #f8f9fa;
  font-weight: 600;
  color: #495057;
}

/* Striped tables for better readability */
tbody tr:nth-child(even) {
  background: #f8f9fa;
}

/* List improvements */
ul {
  list-style-type: disc;
}

ol {
  list-style-type: decimal;
}

ul ul, ol ol, ul ol, ol ul {
  margin: 0.5em 0;
}

/* Task list styling */
.contains-task-list {
  list-style: none;
  padding-left: 0;
}

.task-list-item {
  list-style: none;
  margin: 0.5em 0;
}

.task-list-item input[type="checkbox"] {
  margin-right: 0.5em;
}

/* Better emphasis */
strong, b {
  font-weight: 700;
  color: #1a202c;
}

em, i {
  font-style: italic;
  color: #4a5568;
}

/* Horizontal rules */
hr {
  border: none;
  height: 1px;
  background: #e2e8f0;
  margin: 2em 0;
}
`;
  }
}
